import { Grid, GridSize, MenuItem } from "@material-ui/core";
import { Field, FormikErrors, FormikTouched } from "formik";
import * as React from "react";
import { discoveryOptions, getJobCategoryOptions } from "../../constants";
import { t } from "../../i18n/util";
import { applicationStore } from "../../stores/ApplicationStore";
import { IGetApplicantDetailsResponse, JobCategoryEnum, PostApplicantPayload } from "../../types/models";
import { CustomInputField } from "../ui/CustomInputField";
import { ThreeFieldGridItem } from "../ui/Primitives";
import { getSortedTranslatedOptions } from "../util/helpers";

type Props = {
    errors: FormikErrors<PostApplicantPayload | IGetApplicantDetailsResponse>;
    touched: FormikTouched<PostApplicantPayload | IGetApplicantDetailsResponse>;
    lastFieldMd?: GridSize;
    lastFieldLg?: GridSize;
    children?: React.ReactNode;
    showCustomDiscoverySourceField?: boolean;
    customDiscoverySourceMd?: GridSize;
    customDiscoverySourceLg?: GridSize;
};

export const PersonalDataGrid = ({
    errors,
    touched,
    lastFieldMd,
    lastFieldLg,
    children,
    showCustomDiscoverySourceField,
    customDiscoverySourceMd,
    customDiscoverySourceLg
}: Props) => (
    <Grid
        container
        spacing={2}
        style={{
            marginBottom: 16
        }}
    >
        <ThreeFieldGridItem>
            <Field
                component={CustomInputField}
                label={t("screen.personalData.firstName")}
                name="firstName"
                type="text"
                errorMessage={errors.firstName}
                isTouched={touched.firstName}
                required
            />
        </ThreeFieldGridItem>
        <ThreeFieldGridItem>
            <Field
                component={CustomInputField}
                label={t("screen.personalData.lastName")}
                name="lastName"
                type="text"
                errorMessage={errors.lastName}
                isTouched={touched.lastName}
                required
            />
        </ThreeFieldGridItem>
        <ThreeFieldGridItem>
            <Field
                component={CustomInputField}
                label={t("screen.personalData.jobCategory")}
                name="jobCategory"
                select
                errorMessage={errors.jobCategory}
                isTouched={touched.jobCategory}
                required
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    applicationStore.setJobCategory(e.target.value as JobCategoryEnum);
                }}
            >
                {getSortedTranslatedOptions(getJobCategoryOptions()).map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Field>
        </ThreeFieldGridItem>
        <ThreeFieldGridItem>
            <Field
                component={CustomInputField}
                label={t("screen.personalData.phone")}
                name="phone"
                type="tel"
                errorMessage={errors.phone}
                isTouched={touched.phone}
                required
            />
        </ThreeFieldGridItem>
        <ThreeFieldGridItem>
            <Field
                component={CustomInputField}
                label={t("screen.personalData.email")}
                name="email"
                type="email"
                errorMessage={errors.email}
                isTouched={touched.email}
                required
            />
        </ThreeFieldGridItem>
        <ThreeFieldGridItem>
            <Field
                component={CustomInputField}
                label={t("screen.personalData.website")}
                name="previousProjects"
                errorMessage={errors.previousProjects}
                isTouched={touched.previousProjects}
                optional
                type="text"
            />
        </ThreeFieldGridItem>
        <Grid item xs={12} sm={12} md={lastFieldMd ?? 12} lg={lastFieldLg ?? 12}>
            <Field
                component={CustomInputField}
                label={t("screen.personalData.discovery")}
                name="discovery"
                errorMessage={errors.discovery}
                isTouched={touched.discovery}
                select
                required
            >
                {discoveryOptions.map((option, index) => (
                    <MenuItem key={index} value={option.value}>
                        {t(option.messageId)}
                    </MenuItem>
                ))}
            </Field>
        </Grid>
        {showCustomDiscoverySourceField && (
            <Grid item xs={12} sm={12} md={customDiscoverySourceMd ?? 12} lg={customDiscoverySourceLg ?? 12}>
                <Field
                    component={CustomInputField}
                    label={t("screen.personalData.differentDiscoverySource")}
                    name="customDiscoverySource"
                    errorMessage={errors.customDiscoverySource}
                    isTouched={touched.customDiscoverySource}
                    optional
                    type="text"
                />
            </Grid>
        )}
        {children}
    </Grid>
);
